import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  ArrowLeft, ArrowUpRight, LogOut, RefreshCw, Save, RotateCcw, Info, History,
} from "lucide-react";
import {
  PRICING as DEFAULT_PRICING,
  SIZE_CLASSES as DEFAULT_SIZE_CLASSES,
  QUANTITY_TIERS as DEFAULT_QUANTITY_TIERS,
  URGENCY as DEFAULT_URGENCY,
} from "@/lib/pricing";
import { usePricing } from "@/lib/PricingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "fk_pricing_admin_token";

// ---------- helpers ----------
const num = (v) => (Number.isFinite(+v) ? +v : 0);

function toState(pricing, sizeClasses, tiers, urgency) {
  return {
    pricing: Object.fromEntries(
      Object.entries(pricing).map(([k, v]) => [k, {
        label_de: v.label_de || "",
        label_en: v.label_en || "",
        label_fr: v.label_fr || "",
        label_it: v.label_it || "",
        base_chf_per_dm2: v.base_chf_per_dm2 ?? 0,
        setup_chf: v.setup_chf ?? 0,
        min_stueck_chf: v.min_stueck_chf ?? 0,
      }])
    ),
    size_classes: Object.fromEntries(
      Object.entries(sizeClasses).map(([k, v]) => [k, {
        label_de: v.label_de || "",
        label_en: v.label_en || "",
        label_fr: v.label_fr || "",
        label_it: v.label_it || "",
        dm2: v.dm2 ?? 0,
      }])
    ),
    quantity_tiers: tiers.map((t) => ({ min: t.min ?? 0, pct: t.pct ?? 0 })),
    urgency: Object.fromEntries(
      Object.entries(urgency).map(([k, v]) => [k, {
        label_de: v.label_de || "",
        label_en: v.label_en || "",
        label_fr: v.label_fr || "",
        label_it: v.label_it || "",
        pct: v.pct ?? 0,
      }])
    ),
  };
}

// ---------- Login Screen ----------
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pricing-admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login fehlgeschlagen.");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="min-h-screen bg-[#0E0E10] text-white flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-pricing-login-form" className="w-full max-w-md bg-[#121214] border border-white/10 p-10">
        <div className="overline mb-3">— Fritz Kamm AG · Preise verwalten</div>
        <h1 className="font-display text-3xl tracking-tight mb-8">Anmelden</h1>
        <label className="overline block mb-2">Passwort</label>
        <input type="password" data-testid="admin-pricing-password" className="field mb-8" value={password}
          onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" autoFocus required />
        <button type="submit" disabled={submitting} data-testid="admin-pricing-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-4 text-sm font-semibold hover:bg-zinc-200 transition-colors disabled:opacity-60">
          {submitting ? "Wird geprüft…" : "Anmelden"} <ArrowUpRight className="w-4 h-4" />
        </button>
        <a href="/" className="mt-6 inline-flex items-center gap-2 text-[12px] text-zinc-500 hover:text-white transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Zur Website
        </a>
      </form>
    </div>
  );
}

// ---------- Editor ----------
const HISTORY_LABELS = {
  pricing: "Verfahren",
  size_classes: "Grössenklassen",
  quantity_tiers: "Mengenrabatte",
  urgency: "Termin-Aufschläge",
};

function formatHistoryDate(value) {
  try {
    return new Intl.DateTimeFormat("de-CH", {
      dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Zurich",
    }).format(new Date(value));
  } catch (_) { return value; }
}

function Editor({ token, onLogout }) {
  const { refresh: refreshLive } = usePricing();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/pricing-admin/history`, auth);
      setHistory(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pricing/config`);
      // Merge overrides on top of defaults for the editor form
      const merged = {
        pricing:       { ...DEFAULT_PRICING,        ...(data.pricing || {}) },
        size_classes:  { ...DEFAULT_SIZE_CLASSES,   ...(data.size_classes || {}) },
        quantity_tiers: (data.quantity_tiers && data.quantity_tiers.length) ? data.quantity_tiers : DEFAULT_QUANTITY_TIERS,
        urgency:       { ...DEFAULT_URGENCY,        ...(data.urgency || {}) },
      };
      // deep merge nested verfahren so partial overrides keep defaults
      Object.keys(DEFAULT_PRICING).forEach((k) => {
        merged.pricing[k] = { ...DEFAULT_PRICING[k], ...(data.pricing?.[k] || {}) };
      });
      Object.keys(DEFAULT_SIZE_CLASSES).forEach((k) => {
        merged.size_classes[k] = { ...DEFAULT_SIZE_CLASSES[k], ...(data.size_classes?.[k] || {}) };
      });
      Object.keys(DEFAULT_URGENCY).forEach((k) => {
        merged.urgency[k] = { ...DEFAULT_URGENCY[k], ...(data.urgency?.[k] || {}) };
      });
      setState(toState(merged.pricing, merged.size_classes, merged.quantity_tiers, merged.urgency));
      await loadHistory();
    } catch (_) {
      toast.error("Konfiguration konnte nicht geladen werden.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!state) return;
    setSaving(true);
    try {
      // Normalise numeric fields
      const payload = {
        pricing: Object.fromEntries(Object.entries(state.pricing).map(([k, v]) => [k, {
          ...v,
          base_chf_per_dm2: num(v.base_chf_per_dm2),
          setup_chf: num(v.setup_chf),
          min_stueck_chf: num(v.min_stueck_chf),
        }])),
        size_classes: Object.fromEntries(Object.entries(state.size_classes).map(([k, v]) => [k, {
          ...v, dm2: num(v.dm2),
        }])),
        quantity_tiers: state.quantity_tiers
          .map((t) => ({ min: num(t.min), pct: num(t.pct) }))
          .filter((t) => t.min > 0)
          .sort((a, b) => a.min - b.min),
        urgency: Object.fromEntries(Object.entries(state.urgency).map(([k, v]) => [k, {
          ...v, pct: num(v.pct),
        }])),
      };
      await axios.put(`${API}/pricing-admin/config`, payload, auth);
      await loadHistory();
      toast.success("Preise gespeichert — Website wurde live aktualisiert.");
      refreshLive?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Speichern fehlgeschlagen.";
      toast.error(detail);
      if (err?.response?.status === 401) onLogout();
    } finally { setSaving(false); }
  };

  const resetToDefaults = () => {
    if (!window.confirm("Alle Werte auf Werkseinstellungen zurücksetzen? (Wird nach dem Speichern wirksam.)")) return;
    setState(toState(DEFAULT_PRICING, DEFAULT_SIZE_CLASSES, DEFAULT_QUANTITY_TIERS, DEFAULT_URGENCY));
    toast.info("Auf Werkseinstellungen gesetzt. Bitte 'Speichern' klicken.");
  };

  if (loading || !state) {
    return (
      <div className="min-h-screen bg-[#0E0E10] text-white flex items-center justify-center">
        <div className="animate-pulse text-zinc-400">Preise werden geladen …</div>
      </div>
    );
  }

  // ---------- render ----------
  return (
    <div className="min-h-screen bg-[#0E0E10] text-white pb-24">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0E0E10]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div>
            <div className="overline mb-1">— Fritz Kamm AG · Admin</div>
            <div className="font-display text-xl md:text-2xl tracking-tight">Preise verwalten</div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/admin" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-zinc-300 hover:text-white transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Anfragen
            </a>
            <button type="button" onClick={load} data-testid="admin-pricing-refresh"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-zinc-300 hover:text-white transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Neu laden
            </button>
            <button type="button" onClick={onLogout} data-testid="admin-pricing-logout"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-zinc-300 hover:text-white transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10">
        {/* Info banner */}
        <div className="mb-8 flex items-start gap-3 text-[13px] text-zinc-400 leading-relaxed border-l-2 border-white/20 pl-4 max-w-3xl">
          <Info className="w-4 h-4 flex-none mt-0.5" />
          <div>
            Ändern Sie die Werte unten und klicken Sie auf <strong className="text-white">Speichern</strong>.
            Die Änderungen erscheinen sofort im Preis-Rechner (<code>#preis</code>) und der KI-Analyse (<code>#ki-analyse</code>) — kein Neustart nötig.
            <br />
            <strong className="text-white">Grundpreis</strong> = CHF pro dm² Oberfläche · <strong className="text-white">Rüstkosten</strong> = einmalige Kosten pro Auftrag · <strong className="text-white">Min. Stückpreis</strong> = wird nie unterschritten.
          </div>
        </div>

        {/* ---------- Änderungsprotokoll ---------- */}
        <section data-testid="admin-pricing-history" className="mb-14 max-w-3xl">
          <div className="overline mb-4">00 · Änderungsprotokoll</div>
          <div className="border border-white/10 bg-[#121214]">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 text-[11px] uppercase tracking-[0.14em] text-zinc-500">
              <History className="w-3.5 h-3.5" /> Gespeicherte Preisänderungen
            </div>
            {history.length === 0 ? (
              <p className="px-4 py-5 text-sm text-zinc-400">Noch keine Preisänderung protokolliert.</p>
            ) : (
              <div className="divide-y divide-white/10">
                {history.map((entry, index) => (
                  <div key={`${entry.changed_at}-${index}`} className="px-4 py-4 text-sm">
                    <div className="font-medium text-white">{formatHistoryDate(entry.changed_at)}</div>
                    <div className="mt-1 text-zinc-400">Geänderte Bereiche: {(entry.changes || []).map((change) => HISTORY_LABELS[change.section] || change.section).join(", ") || "Preiswerte"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="mt-3 text-xs leading-relaxed text-zinc-500">Datum und Uhrzeit werden automatisch beim Speichern erfasst. Das Protokoll ist nur in diesem geschützten Preisbereich sichtbar.</p>
        </section>

        {/* ---------- Verfahren ---------- */}
        <section data-testid="admin-pricing-verfahren" className="mb-14">
          <div className="overline mb-4">01 · Verfahren</div>
          <div className="border border-white/10 divide-y divide-white/10">
            {/* header */}
            <div className="hidden md:grid grid-cols-12 gap-4 bg-white/5 px-4 py-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              <div className="col-span-4">Verfahren (Anzeige-Name DE)</div>
              <div className="col-span-2">Grundpreis (CHF/dm²)</div>
              <div className="col-span-2">Rüstkosten (CHF)</div>
              <div className="col-span-2">Min. Stückpreis (CHF)</div>
              <div className="col-span-2">Key</div>
            </div>
            {Object.entries(state.pricing).map(([key, v]) => (
              <div key={key} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 items-center bg-[#121214]">
                <div className="md:col-span-4">
                  <input type="text" value={v.label_de} onChange={(e) => setState((s) => ({ ...s, pricing: { ...s.pricing, [key]: { ...s.pricing[key], label_de: e.target.value } } }))}
                    className="field !py-2 !text-sm" data-testid={`price-label-${key}`} />
                </div>
                <div className="md:col-span-2">
                  <input type="number" step="0.1" value={v.base_chf_per_dm2}
                    onChange={(e) => setState((s) => ({ ...s, pricing: { ...s.pricing, [key]: { ...s.pricing[key], base_chf_per_dm2: e.target.value } } }))}
                    className="field !py-2 !text-sm tabular-nums" data-testid={`price-base-${key}`} />
                </div>
                <div className="md:col-span-2">
                  <input type="number" step="1" value={v.setup_chf}
                    onChange={(e) => setState((s) => ({ ...s, pricing: { ...s.pricing, [key]: { ...s.pricing[key], setup_chf: e.target.value } } }))}
                    className="field !py-2 !text-sm tabular-nums" data-testid={`price-setup-${key}`} />
                </div>
                <div className="md:col-span-2">
                  <input type="number" step="0.05" value={v.min_stueck_chf}
                    onChange={(e) => setState((s) => ({ ...s, pricing: { ...s.pricing, [key]: { ...s.pricing[key], min_stueck_chf: e.target.value } } }))}
                    className="field !py-2 !text-sm tabular-nums" data-testid={`price-min-${key}`} />
                </div>
                <div className="md:col-span-2 text-[11px] font-mono text-zinc-500 truncate">{key}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- Grössenklassen ---------- */}
        <section data-testid="admin-pricing-sizes" className="mb-14">
          <div className="overline mb-4">02 · Grössenklassen (Oberfläche in dm²)</div>
          <div className="border border-white/10 divide-y divide-white/10">
            <div className="hidden md:grid grid-cols-12 gap-4 bg-white/5 px-4 py-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              <div className="col-span-5">Bezeichnung DE</div>
              <div className="col-span-3">Oberfläche (dm²)</div>
              <div className="col-span-4">Key</div>
            </div>
            {Object.entries(state.size_classes).map(([key, v]) => (
              <div key={key} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 items-center bg-[#121214]">
                <div className="md:col-span-5">
                  <input type="text" value={v.label_de}
                    onChange={(e) => setState((s) => ({ ...s, size_classes: { ...s.size_classes, [key]: { ...s.size_classes[key], label_de: e.target.value } } }))}
                    className="field !py-2 !text-sm" data-testid={`size-label-${key}`} />
                </div>
                <div className="md:col-span-3">
                  <input type="number" step="0.05" value={v.dm2}
                    onChange={(e) => setState((s) => ({ ...s, size_classes: { ...s.size_classes, [key]: { ...s.size_classes[key], dm2: e.target.value } } }))}
                    className="field !py-2 !text-sm tabular-nums" data-testid={`size-dm2-${key}`} />
                </div>
                <div className="md:col-span-4 text-[11px] font-mono text-zinc-500 truncate">{key}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- Mengenrabatt-Stufen ---------- */}
        <section data-testid="admin-pricing-tiers" className="mb-14">
          <div className="overline mb-4">03 · Mengenrabatt-Stufen (%)</div>
          <div className="border border-white/10 divide-y divide-white/10">
            <div className="hidden md:grid grid-cols-12 gap-4 bg-white/5 px-4 py-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              <div className="col-span-6">Ab Stückzahl</div>
              <div className="col-span-6">Rabatt (%)</div>
            </div>
            {state.quantity_tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-2 md:grid-cols-12 gap-4 px-4 py-4 items-center bg-[#121214]">
                <div className="md:col-span-6">
                  <input type="number" step="1" min="0" value={t.min}
                    onChange={(e) => setState((s) => {
                      const arr = [...s.quantity_tiers]; arr[i] = { ...arr[i], min: e.target.value }; return { ...s, quantity_tiers: arr };
                    })} className="field !py-2 !text-sm tabular-nums" data-testid={`tier-min-${i}`} />
                </div>
                <div className="md:col-span-6">
                  <input type="number" step="0.5" value={t.pct}
                    onChange={(e) => setState((s) => {
                      const arr = [...s.quantity_tiers]; arr[i] = { ...arr[i], pct: e.target.value }; return { ...s, quantity_tiers: arr };
                    })} className="field !py-2 !text-sm tabular-nums" data-testid={`tier-pct-${i}`} />
                </div>
              </div>
            ))}
            <div className="flex gap-2 px-4 py-3 bg-white/5">
              <button type="button" onClick={() => setState((s) => ({ ...s, quantity_tiers: [...s.quantity_tiers, { min: 0, pct: 0 }] }))}
                data-testid="tier-add" className="px-3 py-1.5 border border-white/15 hover:border-white/40 text-[11px] uppercase tracking-[0.14em] text-zinc-300 hover:text-white transition-colors">
                + Stufe hinzufügen
              </button>
              {state.quantity_tiers.length > 0 && (
                <button type="button" onClick={() => setState((s) => ({ ...s, quantity_tiers: s.quantity_tiers.slice(0, -1) }))}
                  data-testid="tier-remove" className="px-3 py-1.5 border border-white/15 hover:border-white/40 text-[11px] uppercase tracking-[0.14em] text-zinc-400 hover:text-white transition-colors">
                  – Letzte entfernen
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ---------- Termin-Aufschläge ---------- */}
        <section data-testid="admin-pricing-urgency" className="mb-14">
          <div className="overline mb-4">04 · Termin-Aufschläge (%)</div>
          <div className="border border-white/10 divide-y divide-white/10">
            <div className="hidden md:grid grid-cols-12 gap-4 bg-white/5 px-4 py-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              <div className="col-span-6">Bezeichnung DE</div>
              <div className="col-span-3">Aufschlag (%)</div>
              <div className="col-span-3">Key</div>
            </div>
            {Object.entries(state.urgency).map(([key, v]) => (
              <div key={key} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 items-center bg-[#121214]">
                <div className="md:col-span-6">
                  <input type="text" value={v.label_de}
                    onChange={(e) => setState((s) => ({ ...s, urgency: { ...s.urgency, [key]: { ...s.urgency[key], label_de: e.target.value } } }))}
                    className="field !py-2 !text-sm" data-testid={`urgency-label-${key}`} />
                </div>
                <div className="md:col-span-3">
                  <input type="number" step="1" value={v.pct}
                    onChange={(e) => setState((s) => ({ ...s, urgency: { ...s.urgency, [key]: { ...s.urgency[key], pct: e.target.value } } }))}
                    className="field !py-2 !text-sm tabular-nums" data-testid={`urgency-pct-${key}`} />
                </div>
                <div className="md:col-span-3 text-[11px] font-mono text-zinc-500 truncate">{key}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Sticky save bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0B0B0D]/95 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <button type="button" onClick={resetToDefaults} data-testid="admin-pricing-reset"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] text-zinc-300 hover:text-white transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> Auf Werkseinstellungen zurücksetzen
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="admin-pricing-save"
            className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 text-sm font-semibold hover:bg-zinc-200 transition-colors disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Page shell ----------
export default function AdminPricing() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const onLogout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };
  return (
    <>
      {token ? <Editor token={token} onLogout={onLogout} /> : <LoginScreen onLogin={setToken} />}
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#141416", color: "#fff", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0 } }} />
    </>
  );
}
